import React, { useState } from 'react';
import { Alert, Skeleton, Button, Descriptions, Space, Typography, Divider } from 'antd';
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  ReloadOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { trpc } from '../../lib/trpc';
import dayjs from 'dayjs';
import { withDevModalFilePath } from '../common';
import type { ManagedPlanCode } from '@shared/config/subscription-plans';

const { Text } = Typography;
const { Modal } = withDevModalFilePath('client/src/components/company/PlanChangeConfirmModal.tsx');

interface PlanChangeConfirmModalProps {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => Promise<void>;
  currentPlanCode: ManagedPlanCode;
  newPlanCode: ManagedPlanCode;
  loading?: boolean;
}

const PLAN_LABELS: Record<ManagedPlanCode, string> = {
  starter: 'スターター',
  standard: 'スタンダード',
  professional: 'プロフェッショナル',
  enterprise: 'エンタープライズ',
  enterprise_premium: 'エンタープライズプレミアム',
};

const formatCurrency = (amount?: number | null) => {
  if (typeof amount !== 'number' || Number.isNaN(amount)) {
    return '--';
  }

  return `¥${amount.toLocaleString('ja-JP')}`;
};

const formatDate = (dateString: string) => {
  return dayjs(dateString).format('YYYY年MM月DD日');
};

const PlanChangeConfirmModal: React.FC<PlanChangeConfirmModalProps> = ({
  open,
  onCancel,
  onConfirm,
  currentPlanCode,
  newPlanCode,
  loading = false,
}) => {
  const [isConfirming, setIsConfirming] = useState(false);

  const {
    data: preview,
    isLoading: isLoadingPreview,
    error,
    refetch,
  } = trpc.stripe.previewPlanChange.useQuery(
    { newPlanCode },
    { enabled: open && !!newPlanCode }
  );

  const handleConfirm = async () => {
    setIsConfirming(true);
    try {
      await onConfirm();
    } finally {
      setIsConfirming(false);
    }
  };

  const isUpgrade = preview?.changeType === 'upgrade';
  const isDowngrade = preview?.changeType === 'downgrade';
  const isSame = preview?.changeType === 'same';

  const currentPlanLabel = PLAN_LABELS[currentPlanCode] ?? currentPlanCode;
  const newPlanLabel = PLAN_LABELS[newPlanCode] ?? newPlanCode;

  const isProcessing = isConfirming || loading;

  return (
    <Modal
      title={
        <Space>
          {isUpgrade && <ArrowUpOutlined style={{ color: '#52c41a' }} />}
          {isDowngrade && <ArrowDownOutlined style={{ color: '#faad14' }} />}
          プラン変更の確認
        </Space>
      }
      open={open}
      onCancel={onCancel}
      closable={!isProcessing}
      maskClosable={!isProcessing}
      width={600}
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={isProcessing}>
          キャンセル
        </Button>,
        <Button
          key="confirm"
          type="primary"
          onClick={handleConfirm}
          loading={isProcessing}
          disabled={
            isLoadingPreview ||
            !!error ||
            isSame ||
            (isDowngrade && preview?.validationResult && !preview.validationResult.canDowngrade)
          }
          danger={isDowngrade}
        >
          プラン変更を実行
        </Button>,
      ]}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* ローディング状態 */}
        {isLoadingPreview && (
          <div data-testid="plan-change-skeleton">
            <Skeleton active paragraph={{ rows: 6 }} />
          </div>
        )}

        {/* エラー状態 */}
        {error && (
          <Alert
            type="error"
            message="プレビュー情報の取得に失敗しました"
            description={error.message}
            action={
              <Button
                size="small"
                type="primary"
                onClick={() => refetch()}
                icon={<ReloadOutlined />}
              >
                再試行
              </Button>
            }
          />
        )}

        {/* プレビュー表示 */}
        {preview && !isSame && (
          <>
            {/* プラン変更概要 */}
            <div>
              <Text strong style={{ fontSize: 14 }}>現在のプラン</Text>
              <div style={{ padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px', marginTop: '8px' }}>
                <Text style={{ fontSize: 16 }}>{currentPlanLabel}</Text>
                <br />
                <Text>月額 {formatCurrency(preview.currentPlan.amount)}（税抜）</Text>
              </div>
            </div>

            <div style={{ textAlign: 'center', fontSize: '24px', color: '#999' }}>
              {isUpgrade ? '↑' : '↓'}
            </div>

            <div>
              <Text strong style={{ fontSize: 14 }}>新しいプラン</Text>
              <div
                style={{
                  padding: '12px',
                  backgroundColor: isUpgrade ? '#f6ffed' : '#fff7e6',
                  border: `2px solid ${isUpgrade ? '#52c41a' : '#faad14'}`,
                  borderRadius: '4px',
                  marginTop: '8px',
                }}
              >
                <Text style={{ fontSize: 16, fontWeight: 'bold' }}>{newPlanLabel}</Text>
                <br />
                <Text>月額 {formatCurrency(preview.newPlan.amount)}（税抜）</Text>
              </div>
            </div>

            <Divider />

            {/* アップグレード詳細 */}
            {isUpgrade && preview.prorationDetails && (
              <Alert
                type="success"
                icon={<CheckCircleOutlined />}
                message="アップグレード"
                description={
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Text>✓ プラン変更は即時適用されます</Text>
                    <Text>✓ 差額を日割り計算で請求します</Text>

                    <Divider style={{ margin: '8px 0' }} />

                    <Descriptions column={1} size="small" bordered>
                      <Descriptions.Item label="請求期間">
                        {formatDate(preview.prorationDetails.currentPeriodStart)} 〜{' '}
                        {formatDate(preview.prorationDetails.currentPeriodEnd)}
                      </Descriptions.Item>
                      <Descriptions.Item label="日割り差額請求">
                        <Text strong style={{ fontSize: 16, color: '#52c41a' }}>
                          {formatCurrency(preview.prorationDetails.prorationAmount)}（税抜）
                        </Text>
                        <br />
                        <Text type="secondary">
                          {formatCurrency(Math.floor(preview.prorationDetails.prorationAmount * 1.1))}（税込）
                        </Text>
                      </Descriptions.Item>
                      <Descriptions.Item label="次回以降の月額">
                        <Text>{formatCurrency(preview.newPlan.amount)}（税抜）</Text>
                        <br />
                        <Text type="secondary">
                          {formatCurrency(Math.floor(preview.newPlan.amount * 1.1))}（税込）
                        </Text>
                      </Descriptions.Item>
                    </Descriptions>
                  </Space>
                }
              />
            )}

            {/* ダウングレード制約チェック失敗時 */}
            {isDowngrade && preview.validationResult && !preview.validationResult.canDowngrade && (
              <Alert
                type="error"
                icon={<StopOutlined />}
                message="ダウングレードできません"
                description={
                  <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Text>
                      現在の使用状況が新しいプランの制限を超えているため、ダウングレードできません。
                      以下の項目を削減してから再度お試しください。
                    </Text>

                    {preview.validationResult.violations.map((v, idx) => (
                      <div key={idx}>
                        <Text strong>
                          {v.type === 'equipment' ? '設備数' : 'ストレージ容量'} が超過しています
                        </Text>
                        <Descriptions column={1} size="small" bordered style={{ marginTop: 8 }}>
                          <Descriptions.Item label="現在の使用状況">
                            {v.currentValue} {v.unit}
                          </Descriptions.Item>
                          <Descriptions.Item label="新プランの上限">
                            {v.newLimit} {v.unit}
                          </Descriptions.Item>
                          <Descriptions.Item label="削減が必要な量">
                            <Text type="danger" strong>
                              {v.excessAmount} {v.unit}
                            </Text>
                          </Descriptions.Item>
                        </Descriptions>

                        {v.type === 'equipment' && (
                          <Alert
                            type="info"
                            showIcon={false}
                            message={
                              <Space>
                                <Text>設備を削除するには</Text>
                                <a href="/app/equipments" target="_blank" rel="noopener noreferrer">
                                  設備一覧ページ
                                </a>
                                <Text>から不要な設備を削除してください。</Text>
                              </Space>
                            }
                            style={{ marginTop: 8 }}
                          />
                        )}

                        {v.type === 'storage' && (
                          <Alert
                            type="info"
                            showIcon={false}
                            message={
                              <Space>
                                <Text>ストレージを削減するには</Text>
                                <a href="/app/settings/storage" target="_blank" rel="noopener noreferrer">
                                  ストレージ管理ページ
                                </a>
                                <Text>から不要なファイルを削除してください。</Text>
                              </Space>
                            }
                            style={{ marginTop: 8 }}
                          />
                        )}
                      </div>
                    ))}
                  </Space>
                }
              />
            )}

            {/* ダウングレード詳細 */}
            {isDowngrade && (!preview.validationResult || preview.validationResult.canDowngrade) && (
              <Alert
                type="warning"
                icon={<WarningOutlined />}
                message="ダウングレード"
                description={
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Text>⚠️ 現在のプランは月末まで継続します</Text>
                    <Text>✓ 新しいプランは翌月から適用されます</Text>

                    <Divider style={{ margin: '8px 0' }} />

                    <Descriptions column={1} size="small" bordered>
                      <Descriptions.Item label="現プラン終了日">
                        {formatDate(preview.effectiveDate)}
                      </Descriptions.Item>
                      <Descriptions.Item label="新プラン開始日">
                        {dayjs(preview.effectiveDate).add(1, 'day').format('YYYY年MM月DD日')}
                      </Descriptions.Item>
                      <Descriptions.Item label="次回請求額">
                        <Text strong style={{ fontSize: 16 }}>
                          {formatCurrency(preview.newPlan.amount)}（税抜）
                        </Text>
                        <br />
                        <Text type="secondary">
                          {formatCurrency(Math.floor(preview.newPlan.amount * 1.1))}（税込）
                        </Text>
                      </Descriptions.Item>
                    </Descriptions>

                    <Alert
                      type="info"
                      showIcon={false}
                      message="※ 既にお支払いいただいた今月分の返金はありません"
                      style={{ marginTop: '8px' }}
                    />
                  </Space>
                }
              />
            )}
          </>
        )}

        {/* 同額変更時 */}
        {preview && isSame && (
          <Alert
            type="info"
            message="プラン変更なし"
            description="選択されたプランは現在のプランと同じです。"
          />
        )}
      </Space>
    </Modal>
  );
};

export default PlanChangeConfirmModal;
